import { CmsMeta } from '../cmsMeta';
import { useEffect, useRef, useState } from 'react';
import { Icon, chevron_down, chevron_up } from '../../Icon/Icon';
import SanitizedHtml from '../../SanitizedHtml/SanitizedHtml';
import './CmsFaqQuestion.css';

type CmsFaqQuestionProps = {
	question: string;
	answer?: string;
	answerCompany?: string;
	answerPrivate?: string;
};

export const CmsFaqQuestion = ({
	question,
	answer,
	answerCompany,
	answerPrivate,
}: CmsFaqQuestionProps): JSX.Element => {
	const rootRef = useRef<HTMLDivElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isPrivateSelected, setIsPrivateSelected] = useState(false);
	const [isCompanySelected, setIsCompanySelected] = useState(false);

	const trimmedAnswer = answer === '<br>' ? '' : answer?.trim();
	const trimmedCompanyAnswer = answerCompany === '<br>' ? '' : answerCompany?.trim();
	const trimmedPrivateAnswer = answerPrivate === '<br>' ? '' : answerPrivate?.trim();
	const hasDefaultAnswer = Boolean(trimmedAnswer);
	const hasCompanyAnswer = Boolean(trimmedCompanyAnswer);
	const hasPrivateAnswer = Boolean(trimmedPrivateAnswer);
	const hideForPrivateAudience = isPrivateSelected && !hasDefaultAnswer && !hasPrivateAnswer;
	const hideForCompanyAudience = isCompanySelected && !hasDefaultAnswer && !hasCompanyAnswer;
	const toggle = () => setIsOpen((prev) => !prev);

	useEffect(() => {
		const faqRoot = rootRef.current?.closest('.cms-faq');

		if (!faqRoot) return;

		const updateAudience = () => {
			setIsPrivateSelected(faqRoot.classList.contains('cms-faq--private'));
			setIsCompanySelected(faqRoot.classList.contains('cms-faq--company'));
		};

		updateAudience();

		if (typeof MutationObserver === 'undefined') return;

		const observer = new MutationObserver(updateAudience);
		observer.observe(faqRoot, { attributes: true, attributeFilter: ['class'] });

		return () => observer.disconnect();
	}, []);

	if (hideForPrivateAudience || hideForCompanyAudience) {
		return <></>;
	}

	return (
		<div ref={rootRef} className={`cms-faq-question ${isOpen ? 'cms-faq-question--open' : ''}`}>
			<button className="cms-faq-question__toggle" type="button" onClick={toggle} aria-expanded={isOpen}>
				<span className="cms-faq-question__title">{question}</span>
				<span className="cms-faq-question__icon" aria-hidden="true">
					{isOpen ? (
						<Icon shape={chevron_up} className="favorites-icon-dropdown" />
					) : (
						<Icon shape={chevron_down} className="favorites-icon-dropdown" />
					)}
				</span>
			</button>
			{isOpen && (
				<div className="cms-faq-question__answers">
					{trimmedAnswer ? <SanitizedHtml html={trimmedAnswer} className="cms-faq-question__answer" /> : null}
					{answerCompany ? (
						<SanitizedHtml
							className="cms-faq-question__answer cms-faq-question__answer--company"
							html={answerCompany}
						/>
					) : null}
					{answerPrivate ? (
						<SanitizedHtml
							className="cms-faq-question__answer cms-faq-question__answer--private"
							html={answerPrivate}
						/>
					) : null}
				</div>
			)}
		</div>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		'One question and its answer, inside CmsFaq. Give `answer` alone, or give `answerCompany` and `answerPrivate` instead when the two customer types need different wording - the page then shows whichever matches the reader.',
	example: [
		'CmsFaqQuestion',
		{
			index: 1,
			question: 'Brauche ich ein Geschäftskonto, um bei Ihnen einzukaufen?',
			answer: '',
			answerCompany: 'Nein, Sie brauchen kein Konto, um eine Bestellung aufzugeben.',
			answerPrivate: 'Nein, Sie können auch ohne Konto bestellen.',
		},
	],
};
